/**
 * Some keys have underscores because values come from API
 *
 * POIs:
 * - From Schools POIs
 * -- high_schools & primary_schools
 *
 * - From Enhanced POIs
 * -- alcohol, clothing, common_needs, conference_centers, entertainment, hospitals, libraries, pharmacies, retail, shops_and_services
 */

const namespace = {
  attribution: "Powered by",
  time: {
    minute_one: "Minute",
    minute_other: "Minutes",
    hour_one: "Hour",
    hour_other: "Hours",
  },
  distance: {
    meter: "m",
    kilometer: "km",
    yard: "yd",
    mile: "mi",
  },
  language: {
    english: "English",
    french: "French",
    spanish: "Spanish",
  },
  map: {
    marker: {
      schoolAttributes: {
        levels: "Level",
        grades: "Grades",
        languages: "Languages",
        types: "Type",
        board: "Board",
        programs: "Programs",
      },
      schoolCta: "Link to school",
      district: "District",
    },
  },
  scores: {
    cafes: {
      title: "Cafes",
      titleDeprecated: "Cafes",
      description: "A measure of the quantity of nearby coffee shops.",
    },
    car_friendly: {
      title: "Car friendly",
      titleDeprecated: "Driving",
      description:
        "Represents how well traffic flows, and how easy it is to park.",
    },
    cycling_friendly: {
      title: "Cycling friendly",
      titleDeprecated: "Cycling",
      description:
        "Represents how suitable the area is for cycling in terms of nearby bicycle infrastructure and calm streets, as well as the number of destinations that can be reached by bicycle.",
    },
    daycares: {
      title: "Daycares",
      titleDeprecated: "Day care",
      description: "Represents the distance to the nearest daycare.",
    },
    greenery: {
      title: "Greenery",
      titleDeprecated: "Greenery",
      description: "Quantity of nearby green spaces and tree canopy coverage.",
    },
    groceries: {
      title: "Groceries",
      titleDeprecated: "Grocery",
      description: "A measure of the quantity of nearby grocery stores.",
    },
    high_schools: {
      title: "High schools",
      titleDeprecated: "High schools",
      description: "Represents the distance to nearest high school.",
    },
    historic: {
      title: "Historic",
      titleDeprecated: "Historic",
      description:
        "A measure of the quantity and size of heritage buildings, and the average building age in the area.",
    },
    nightlife: {
      title: "Nightlife",
      titleDeprecated: "Night life",
      description: "Represents the quantity of bars nearby.",
    },
    parks: {
      title: "Parks",
      titleDeprecated: "Parks",
      description: "A measure of the quantity and size of nearby parks.",
    },
    pedestrian_friendly: {
      title: "Pedestrian friendly",
      titleDeprecated: "Walking",
      description:
        "A measure of how many basic services can be reached on foot and how pleasant it is to walk to them.",
    },
    primary_schools: {
      title: "Primary schools",
      titleDeprecated: "Primary schools",
      description:
        "A measure of the distance to the nearest elementary school.",
    },
    quiet: {
      title: "Quiet",
      titleDeprecated: "Quiet",
      description: "A measure of the average levels of noise nearby.",
    },
    restaurants: {
      title: "Restaurants",
      titleDeprecated: "Restaurants",
      description: "Represents the quantity of restaurants nearby.",
    },
    shopping: {
      title: "Shopping",
      titleDeprecated: "Shopping",
      description:
        "A measure of the quantity of fashion and department stores nearby.",
    },
    transit_friendly: {
      title: "Transit friendly",
      titleDeprecated: "Transit",
      description:
        "A measure of the frequency and level of public transit service.",
    },
    vibrant: {
      title: "Vibrancy",
      titleDeprecated: "Vibrancy",
      description: "Represents the level of street activity nearby.",
    },
    wellness: {
      title: "Wellness",
      titleDeprecated: "Wellness",
      description:
        "Represents how conducive a location is to individuals' well-being.",
    },
  },
  pois: {
    groceries: "Grocery",
    groceries_specialized: "Speciality",
    shopping: "Shopping",
    nightlife: "Nightlife",
    restaurants: "Restaurants",
    cafes: "Coffee shops",
    daycares: "Daycares",
    bus_stops: "Bus",
    rapid_transit_stations: "Rapid transit",
    train_stations: "Train",
    parks: "Parks",
    healthy_restaurants: "Healthy restaurants",
    fitness: "Fitness",
    sport_facilities: "Sport facilities",
    health_care: "Health care",
    pet_care: "Pet care",
    car_fueling_stations: "Gas stations",
    art_culture_science: "Art, Culture, Science",
    home_improvement: "Home improvement",
    personal_care: "Personal care",
    high_schools: "High schools",
    primary_schools: "Primary schools",
    alcohol: "Alcohol",
    clothing: "Clothing",
    common_needs: "Common needs",
    conference_centers: "Conference centers",
    entertainment: "Entertainment",
    hospitals: "Hospitals",
    libraries: "Libraries",
    pharmacies: "Pharmacies",
    retail: "Retail",
    shops_and_services: "Shops and services",
  },
  valueDrivers: {
    bus_service_access: "Bus services",
    shops_and_services_access: "Shops and Services",
    coffee_shop_access: "Coffee shops",
    clothing_and_accessory_store_access: "Clothing stores",
    common_needs_access: "Common necessities",
    department_store_access: "Department stores",
    entertainment_venue_access: "Entertainment venues",
    fitness_establishment_access: "Fitness",
    nightlife_access: "Nightlife",
    pedestrian_oriented_street_access: "Walkable streets",
    rapid_transit_service_access: "Rapid transit service",
    restaurant_access: "Restaurants",
    specialty_grocery_store_access: "Specialty groceries",
    sports_facility_access: "Sport facilities",
    train_service_access: "Train stations",
    nearest_highway_proximity: "Highway access",
    nearest_pharmacy_proximity: "Nearby pharmacies",
    nearest_bicycle_share_station_proximity: "Nearby bikeshare stations",
    bicycle_path_access: "Bike path access",
    bicycle_share_station_access: "Bike share",
    nearest_park_area: "Nearby large park",
    bicycle_infrastructure_quality: "Bike infrastructure",
    high_schools: "High schools",
    primary_schools: "Elementary schools",
    quiet: "Quiet",
    daycares: "Daycares",
    parks: "Parks",
  },
} as const;

export default namespace;
